import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "motion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faXmark,
  faFolder,
  faServer,
  faUsers,
  faChartMixed,
  faTableColumns,
  faFileLines,
  faRocket,
  faFilters,
  faChartLine,
  faPieChart,
  faAddressCard,
  faBolt,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { useNavigate } from "react-router";
import { useDebouncedValue } from "@/hooks/useDebounce";
import { fetchScreenerSearch } from "@/services/api/screener";
import * as AgenticV2API from "@/services/api/agentic-v2";
import { useTaskPaletteOptional } from "@/context/TaskPaletteContext";
import { useWorkerList } from "@/context/WorkerListContext";
import { useWorkersListGate } from "@/services/api/worker-ani/queries/WorkersListGate";
import { usePodList } from "@/context/PodListContext";
import { useModulePermissions } from "@/context/ModulePermissionsContext";
import WorkerAvatar from "@/components/worker/WorkerAvatar";
import {
  getNavigationAppsByPermission,
  getSpecializedAppsByPermission,
  type AppConfig,
} from "@/config/apps";
import { LoadingIndicator } from "../chat";
import { APP_REGISTRY } from "@/components/ui/os-layout/OsAppRegistry";
import { useOsActionsOptional } from "@/components/ui/os-layout/OsContext";
import {
  buildScreenerAssetSearchDescription,
  dedupeSearchResultsByUrl,
  MAIN_ASSET_TYPES,
  partitionSearchResultsForFilter,
} from "./globalSearchResults";
import { parseUrlToWindowConfig } from "@/components/ui/os-layout/urlMapping";
import type { OpenWindowConfig } from "@/components/ui/os-layout/useWindow";
import { usePlatform } from "@/hooks/usePlatform";

function resolveSearchResultWindowConfig(
  result: SearchResult,
): OpenWindowConfig | null {
  if (result.type === "portfolio" && result.id) {
    return {
      appId: "portfolio",
      title: result.title,
      params: { id: result.id },
    };
  }
  if (result.type === "product" && result.id) {
    return {
      appId: "product",
      title: result.title,
      params: { id: result.id },
    };
  }
  return null;
}

export interface SearchResult {
  id: string;
  title: string;
  description?: string;
  type: SearchResultType;
  url: string;
  icon: React.ReactNode;
  metadata?: SearchResultMetadata;
  /**
   * Optional custom action invoked when the user picks this result.
   * Overrides the default URL navigation. Used for "verb" results like
   * launching a task palette.
   */
  onSelect?: () => void;
}

export interface SearchResultMetadata {
  resource_type?: string;
  currency?: string;
  isin?: string;
  long_name?: string;
  id?: string;
  job_title?: string;
  version?: string;
}

function dropNullSearchResults(
  entries: Array<SearchResult | null>,
): SearchResult[] {
  return entries.filter((entry): entry is SearchResult => entry !== null);
}

function appConfigToSearchResult(app: AppConfig): SearchResult | null {
  if (!parseUrlToWindowConfig(app.path)) {
    return null;
  }
  return {
    id: app.id,
    title: app.title,
    description: app.description,
    type: app.searchType,
    url: app.path,
    icon: <FontAwesomeIcon icon={app.icon} />,
  };
}

export type SearchResultType =
  | "product"
  | "portfolio"
  | "index"
  | "benchmark"
  | "worker"
  | "app"
  | "pod"
  | "dashboard"
  | "screener"
  | "playground"
  | "documents"
  | "ops"
  | "inbox"
  | "reports"
  | "workers"
  | "pods"
  | "home"
  | "client"
  | "task";

interface GlobalSearchDialogProps {
  isOpen: boolean;
  onClose: () => void;
  showOverlay?: boolean;
  /** Pre-filled query applied each time the dialog opens. */
  initialQuery?: string;
}

const SEARCH_CATEGORIES: Record<
  SearchResultType,
  {
    label: string;
    icon: React.ReactNode;
    color: string;
  }
> = {
  product: {
    label: "Products",
    icon: <FontAwesomeIcon icon={faChartLine} />,
    color: "text-blue-600 dark:text-blue-400",
  },
  portfolio: {
    label: "Portfolios",
    icon: <FontAwesomeIcon icon={faPieChart} />,
    color: "text-green-600 dark:text-green-400",
  },
  index: {
    label: "Indexes",
    icon: <FontAwesomeIcon icon={faChartMixed} />,
    color: "text-purple-600 dark:text-purple-400",
  },
  benchmark: {
    label: "Benchmarks",
    icon: <FontAwesomeIcon icon={faChartMixed} />,
    color: "text-orange-600 dark:text-orange-400",
  },
  worker: {
    label: "Workers",
    icon: <FontAwesomeIcon icon={faUsers} />,
    color: "text-indigo-600 dark:text-indigo-400",
  },
  app: {
    label: "Apps",
    icon: <FontAwesomeIcon icon={faRocket} />,
    color: "text-pink-600 dark:text-pink-400",
  },
  pod: {
    label: "Pods",
    icon: <FontAwesomeIcon icon={faServer} />,
    color: "text-red-600 dark:text-red-400",
  },
  dashboard: {
    label: "Quick Navigation",
    icon: <FontAwesomeIcon icon={faTableColumns} />,
    color: "text-slate-600 dark:text-slate-400",
  },
  screener: {
    label: "Quick Navigation",
    icon: <FontAwesomeIcon icon={faFilters} />,
    color: "text-slate-600 dark:text-slate-400",
  },
  playground: {
    label: "Quick Navigation",
    icon: <FontAwesomeIcon icon={faChartMixed} />,
    color: "text-slate-600 dark:text-slate-400",
  },
  documents: {
    label: "Quick Navigation",
    icon: <FontAwesomeIcon icon={faFolder} />,
    color: "text-slate-600 dark:text-slate-400",
  },
  ops: {
    label: "Quick Navigation",
    icon: <FontAwesomeIcon icon={faServer} />,
    color: "text-slate-600 dark:text-slate-400",
  },
  inbox: {
    label: "Quick Navigation",
    icon: <FontAwesomeIcon icon={faRocket} />,
    color: "text-slate-600 dark:text-slate-400",
  },
  reports: {
    label: "Quick Navigation",
    icon: <FontAwesomeIcon icon={faFileLines} />,
    color: "text-slate-600 dark:text-slate-400",
  },
  workers: {
    label: "Quick Navigation",
    icon: <FontAwesomeIcon icon={faUsers} />,
    color: "text-slate-600 dark:text-slate-400",
  },
  pods: {
    label: "Quick Navigation",
    icon: <FontAwesomeIcon icon={faServer} />,
    color: "text-slate-600 dark:text-slate-400",
  },
  home: {
    label: "Quick Navigation",
    icon: <FontAwesomeIcon icon={faTableColumns} />,
    color: "text-slate-600 dark:text-slate-400",
  },
  client: {
    label: "Clients",
    icon: <FontAwesomeIcon icon={faAddressCard} />,
    color: "text-emerald-600 dark:text-emerald-400",
  },
  task: {
    label: "Delegate task",
    icon: <FontAwesomeIcon icon={faBolt} />,
    color: "text-indigo-600 dark:text-indigo-400",
  },
};

export function GlobalSearchDialog({
  isOpen,
  onClose,
  showOverlay = true,
  initialQuery = "",
}: GlobalSearchDialogProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selectedIndexRef = useRef(0);
  const [groupedResults, setGroupedResults] = useState<
    Record<SearchResultType, SearchResult[]>
  >({} as Record<SearchResultType, SearchResult[]>);
  const [displayResults, setDisplayResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<SearchResultType | "all">("all");
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const actionsContext = useOsActionsOptional();
  const openWindow = actionsContext?.openWindow ?? null;
  const debouncedQuery = useDebouncedValue(query, 300);
  const lastSearchedQuery = useRef<string>("");

  // Use contexts for local data
  const { workers, loading: workersLoading } = useWorkerList();
  const { requestList, releaseList } = useWorkersListGate();
  const { pods } = usePodList();
  const { hasPermission } = useModulePermissions();
  const { isMac } = usePlatform();
  const taskPalette = useTaskPaletteOptional();

  // Focus input when dialog opens
  useEffect(() => {
    if (isOpen) {
      requestList();
      // Use a small delay to ensure the dialog is fully rendered
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
      return () => releaseList();
    }
  }, [isOpen, requestList, releaseList]);

  // Focus dialog container for keyboard navigation
  const dialogRef = useRef<HTMLDivElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      setResults([]);
      setDisplayResults([]);
      setSelectedIndex(0);
      setGroupedResults({} as Record<SearchResultType, SearchResult[]>);
      setIsSearching(false);
      setIsApiLoading(false);
      setSelectedFilter("all");
      lastSearchedQuery.current = "";
    } else if (initialQuery) {
      setQuery(initialQuery);
    }
  }, [isOpen, initialQuery]);

  // Search function
  const performSearch = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery.trim() || searchQuery.length < 2) {
        setResults([]);
        setGroupedResults({} as Record<SearchResultType, SearchResult[]>);
        setSelectedFilter("all");
        lastSearchedQuery.current = "";
        return;
      }

      // Don't search if already searching, workers are loading, or we've already searched this query
      if (
        isSearching ||
        workersLoading ||
        lastSearchedQuery.current === searchQuery
      ) {
        return;
      }
      lastSearchedQuery.current = searchQuery;
      setIsSearching(true);
      setIsLoading(true);
      setSelectedFilter("all"); // Reset filter for new search
      const allResults: SearchResult[] = [];

      // Re-publish the running result set into all the various pieces of
      // state the dialog renders from. Called every time we finish another
      // backend fetch so results stream in progressively.
      const publishResults = (current: SearchResult[]) => {
        const deduped = dedupeSearchResultsByUrl(current);
        setResults(deduped);
        const grouped = deduped.reduce((acc, result) => {
          if (!acc[result.type]) acc[result.type] = [];
          acc[result.type].push(result);
          return acc;
        }, {} as Record<SearchResultType, SearchResult[]>);
        setGroupedResults(grouped);
        const quickNavTypes = Object.entries(SEARCH_CATEGORIES)
          .filter(([_, cat]) => cat.label === "Quick Navigation")
          .map(([type]) => type);
        const quickNavResults = quickNavTypes.flatMap(
          (type) => grouped[type as SearchResultType] || [],
        );
        const otherResults = Object.entries(grouped)
          .filter(([type]) => !quickNavTypes.includes(type))
          .flatMap(([_, results]) => results);
        setDisplayResults([...quickNavResults, ...otherResults]);
      };

      // Search for workers using context data (immediate)

      // Only search workers if they're loaded
      if (!workersLoading && workers.length > 0) {
        const filteredWorkers = workers.filter(
          (worker) =>
            worker.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (worker.bio &&
              worker.bio.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (worker.job_title &&
              worker.job_title
                .toLowerCase()
                .includes(searchQuery.toLowerCase()))
        );

        const workerResults: SearchResult[] = filteredWorkers
          .slice(0, 5)
          .map((worker) => ({
            id: worker._id,
            title: worker.name,
            description: worker.bio || worker.job_title || "Worker",
            type: "worker" as SearchResultType,
            url: `/worker/${worker._id}`,
            icon: <WorkerAvatar seed={worker.name} size={28} />,
            metadata: { job_title: worker.job_title },
          }));
        allResults.push(...workerResults);
      }

      // Search for pods using context data (immediate)
      const filteredPods = pods.filter(
        (pod) =>
          pod.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (pod.description &&
            pod.description.toLowerCase().includes(searchQuery.toLowerCase()))
      );

      const podResults: SearchResult[] = filteredPods
        .slice(0, 5)
        .map((pod) => ({
          id: pod._id,
          title: pod.name,
          description: pod.description || "Pod template",
          type: "pod" as SearchResultType,
          url: `/pod/${pod._id}`,
          icon: SEARCH_CATEGORIES.pod.icon,
          metadata: { version: pod.version },
        }));
      allResults.push(...podResults);

      // Module launchers come from navigation + specialized apps (not APP_REGISTRY).
      // Registry includes parametric windows (portfolio, portfolios→screener) that read as fake results.

      // Add navigation apps based on permissions
      const navigationApps = getNavigationAppsByPermission(hasPermission);
      const filteredNavigationApps = navigationApps
        .filter(
          (app) =>
            app.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            app.description.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .map((app) => appConfigToSearchResult(app));
      allResults.push(...dropNullSearchResults(filteredNavigationApps));

      // Add specialized apps based on permissions
      const specializedApps = getSpecializedAppsByPermission(hasPermission);
      const filteredSpecializedApps = specializedApps
        .filter(
          (app) =>
            app.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            app.description.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .map((app) => appConfigToSearchResult(app));
      allResults.push(...dropNullSearchResults(filteredSpecializedApps));

      const immediateResults = dedupeSearchResultsByUrl(allResults);

      // Show immediate results first
      setResults(immediateResults);

      // Group results by type
      const grouped = immediateResults.reduce((acc, result) => {
        if (!acc[result.type]) {
          acc[result.type] = [];
        }
        acc[result.type].push(result);
        return acc;
      }, {} as Record<SearchResultType, SearchResult[]>);

      setGroupedResults(grouped);

      // Create display results array in the same order as they appear in the UI
      const quickNavTypes = Object.entries(SEARCH_CATEGORIES)
        .filter(([_, cat]) => cat.label === "Quick Navigation")
        .map(([type]) => type);
      const quickNavResults = quickNavTypes.flatMap(
        (type) => grouped[type as SearchResultType] || []
      );
      const otherResults = Object.entries(grouped)
        .filter(([type]) => !quickNavTypes.includes(type))
        .flatMap(([_, results]) => results);

      const displayOrder = [...quickNavResults, ...otherResults];
      setDisplayResults(displayOrder);
      setSelectedIndex(0);

      // Stop loading for immediate results, but start API loading
      setIsLoading(false);
      setIsApiLoading(true);

      // Now fetch API results in the background
      try {
        // Search for products, portfolios, and indexes using screener endpoint
        try {
          const screenerResponse = await fetchScreenerSearch(
            searchQuery,
            "display_name",
            "asc",
            1,
            30
          );
          const screenerResults = dropNullSearchResults(
            (screenerResponse.items || []).map((item): SearchResult | null => {
            const resourceId = item.metadata?.resource_id;
            if (!resourceId) {
              return null;
            }

            let type: SearchResultType = "product";
            let url = `/product/${resourceId}`;

            const resourceType = (item.resource_type ?? "product").toLowerCase();

            if (resourceType === "portfolio") {
              type = "portfolio";
              url = `/portfolio/${resourceId}`;
            } else if (resourceType === "index") {
              type = "index";
              url = `/playground?layout=columns&assets=${encodeURIComponent(
                `${item.display_name}:::${resourceId}:::index:::default`
              )}`;
            } else if (resourceType === "benchmark") {
              type = "benchmark";
              url = `/playground?layout=columns&assets=${encodeURIComponent(
                `${item.display_name}:::${resourceId}:::benchmark:::default`
              )}`;
            } else {
              url = `/product/${resourceId}`;
            }

            const currency = item.currency || item.data?.currency;
            const isin = item.data?.isin || item.fundamental_data?.isin || item.isin;
            const longName = item.data?.long_name || item.fundamental_data?.long_name || item.long_name;
            const firstRealDate =
              item.data?.total_return_first_real_date ??
              item.total_return_first_real_date;

            const description = buildScreenerAssetSearchDescription(resourceType, {
              isin: isin ? String(isin) : undefined,
              currency: currency ? String(currency) : undefined,
              firstRealDate: firstRealDate ? String(firstRealDate) : undefined,
            });

            const title = longName || item.display_name || item.name;

            return {
              id: resourceId,
              title,
              description,
              type,
              url,
              icon: SEARCH_CATEGORIES[type].icon,
              metadata: {
                resource_type: item.resource_type,
                currency: currency ? String(currency) : undefined,
                isin: isin ? String(isin) : undefined,
                long_name: longName ? String(longName) : undefined,
                id: item.id,
              },
            };
          }),
          );

          allResults.push(...screenerResults);
          publishResults(allResults);
        } catch (error) {
          console.error("Error searching products/portfolios/indexes:", error);
        }

        // Search agentic-v2 clients + delegatable tasks (in parallel).
        try {
          const [clientsRes, catalogueRes] = await Promise.all([
            AgenticV2API.listClients({ q: searchQuery, limit: 5 }).catch(
              () => [],
            ),
            AgenticV2API.listTaskCatalogue({ q: searchQuery }).catch(
              () => [],
            ),
          ]);

          const clientResults: SearchResult[] = clientsRes.slice(0, 5).map(
            (c) => ({
              id: c._id,
              title: c.display_name,
              description: c.summary || "Client",
              type: "client" as SearchResultType,
              url: `/clients/${c._id}`,
              icon: SEARCH_CATEGORIES.client.icon,
            }),
          );

          const taskResults: SearchResult[] = taskPalette
            ? catalogueRes.slice(0, 5).map((t) => ({
                id: t._id,
                title: `Delegate: ${t.display_name}`,
                description: t.description || "Launch this task",
                type: "task" as SearchResultType,
                // URL is unused (onSelect overrides), but keep something
                // sensible for fallback / accessibility.
                url: "#",
                icon: SEARCH_CATEGORIES.task.icon,
                onSelect: () =>
                  taskPalette.open({ initialCatalogueId: t._id }),
              }))
            : [];

          allResults.push(...clientResults, ...taskResults);
          publishResults(allResults);
        } catch (error) {
          console.error("Error searching clients/tasks:", error);
        }
      } catch (error) {
        console.error("Error performing global search:", error);
      } finally {
        setIsApiLoading(false);
        setIsSearching(false);
      }
    },
    [workersLoading, workers, pods, hasPermission, isSearching, taskPalette]
  );

  // Trigger search when debounced query changes and workers are loaded
  useEffect(() => {
    if (
      !workersLoading &&
      workers.length > 0 &&
      debouncedQuery.trim().length >= 2 &&
      !isSearching
    ) {
      performSearch(debouncedQuery);
    }
  }, [
    debouncedQuery,
    workersLoading,
    workers.length,
    performSearch,
    isSearching,
  ]);

  /**
   * Opens a search result either as a window (if app is supported in OS mode)
   * or navigates to the URL as a fallback.
   * Uses the shared URL mapping utilities for consistency with deep linking.
   */
  const openResultInOsMode = useCallback((result: SearchResult) => {
    // Custom action overrides URL navigation (e.g. opening the task palette).
    if (result.onSelect) {
      result.onSelect();
      return;
    }
    // If OS context is not available, just navigate
    if (!actionsContext || !openWindow) {
      navigate(result.url);
      return;
    }

    // Parse the URL to get window config using shared utility
    const url = new URL(result.url, window.location.origin);
    const windowConfig = parseUrlToWindowConfig(url.pathname, url.search);

    if (!windowConfig) {
      const directConfig = resolveSearchResultWindowConfig(result);
      if (directConfig && APP_REGISTRY[directConfig.appId]) {
        openWindow({
          appId: directConfig.appId,
          title: directConfig.title ?? result.title,
          params: directConfig.params,
        });
        onClose();
        return;
      }
      console.warn(
        "[GlobalSearch] No OS window mapping for result:",
        result.url,
      );
      return;
    }

    const appDef = APP_REGISTRY[windowConfig.appId];
    if (!appDef) {
      console.warn(
        "[GlobalSearch] Unknown OS app for result:",
        windowConfig.appId,
      );
      return;
    }

    let windowSize = appDef.defaultSize;

    if (appDef.defaultSize) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const responsiveWidth = Math.min(
        appDef.defaultSize.width,
        Math.floor(viewportWidth * 0.8),
      );
      const responsiveHeight = Math.min(
        appDef.defaultSize.height,
        Math.floor(viewportHeight * 0.85),
      );

      windowSize = {
        width: responsiveWidth,
        height: responsiveHeight,
      };
    }

    openWindow({
      appId: windowConfig.appId,
      title: result.title,
      params: windowConfig.params,
      initialSize: windowSize,
    });
  }, [actionsContext, openWindow, navigate, onClose]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => {
          const newIndex = Math.min(prev + 1, results.length - 1);
          selectedIndexRef.current = newIndex;
          return newIndex;
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => {
          const newIndex = Math.max(prev - 1, 0);
          selectedIndexRef.current = newIndex;
          return newIndex;
        });
      } else if (e.key === "Enter" && displayResults.length > 0) {
        e.preventDefault();
        const currentSelectedIndex = selectedIndexRef.current;
        const validIndex = Math.max(
          0,
          Math.min(currentSelectedIndex, displayResults.length - 1)
        );
        const selectedResult = displayResults[validIndex];

        if (selectedResult) {
          openResultInOsMode(selectedResult);
          onClose();
        }
      }
    },
    [onClose, displayResults, openResultInOsMode, results.length]
  );

  // Track previous selected index for direction detection
  const prevSelectedIndexRef = useRef(selectedIndex);

  // Update ref when selectedIndex changes
  useEffect(() => {
    selectedIndexRef.current = selectedIndex;
  }, [selectedIndex]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && resultsContainerRef.current) {
      const selectedElement = resultsContainerRef.current.querySelector(
        `[data-selected-index="${selectedIndex}"]`
      ) as HTMLElement;
      if (selectedElement) {
        const isGoingDown = selectedIndex > prevSelectedIndexRef.current;
        const isGoingUp = selectedIndex < prevSelectedIndexRef.current;

        // Always use nearest to avoid aggressive scrolling
        selectedElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });

        // Add a small adjustment after the initial scroll
        setTimeout(() => {
          const container = resultsContainerRef.current;
          if (container) {
            const itemHeight = selectedElement.offsetHeight;
            const currentScrollTop = container.scrollTop;

            if (isGoingDown) {
              // Add a small amount to show more space below
              container.scrollTop = currentScrollTop + itemHeight / 2;
            } else if (isGoingUp) {
              // Subtract a small amount to show more space above
              container.scrollTop = currentScrollTop - itemHeight / 2;
            }
          }
        }, 150);

        // Update previous index
        prevSelectedIndexRef.current = selectedIndex;
      }
    }
  }, [selectedIndex]);

  // Perform search when query changes
  useEffect(() => {
    performSearch(debouncedQuery);
  }, [debouncedQuery, performSearch]);

  const handleResultClick = (result: SearchResult) => {
    openResultInOsMode(result);
    onClose();
  };

  // Only render if we're on the client side
  if (typeof window === "undefined") return null;

  return ReactDOM.createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          key="global-search-wrapper"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[9999]"
        >
          {/* Backdrop */}
          {showOverlay && (
            <motion.div
              className="fixed inset-0 bg-slate-900/20 dark:bg-slate-900/30 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              aria-hidden="true"
              onClick={onClose}
            />
          )}

          {/* Dialog positioned at top, sliding up from bottom */}
          <motion.div
            className="fixed top-1/5 left-1/2 z-50 mx-auto flex w-full max-w-2xl max-h-[80vh] -translate-x-1/2 transform flex-col rounded-3xl border border-slate-200/80 bg-gradient-to-bl from-white/95 to-white/90 shadow-lg shadow-black/10 backdrop-blur-xl dark:border-white/10 dark:from-slate-900/50 dark:to-slate-900/60 dark:shadow-black/20"
           
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{
              type: "spring",
              damping: 25,
              stiffness: 300,
              mass: 0.8,
            }}
          >
            <div
              ref={dialogRef}
              className="p-0"
              onKeyDown={handleKeyDown}
              tabIndex={-1}
            >
              {/* Search Input */}
              <div className="flex items-center border-b border-slate-200 px-6 py-4 dark:border-slate-700">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="mr-3 h-5 w-5 text-slate-500 dark:text-slate-400"
                />
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="flex-1 border-0 bg-transparent text-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-0 dark:text-white"
                  placeholder="Search for something..."
                />
                <div className="flex items-center gap-2 ml-4">
                  {/* Show hotkey hint when input is empty */}
                  {!query && !isApiLoading && (
                    <kbd className="px-1.5 py-0.5 text-sm font-medium text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-700/50 rounded" style={{ letterSpacing: '0.1em' }}>
                      {isMac ? "⌘K" : "Ctrl K"}
                    </kbd>
                  )}
                  {isApiLoading && (
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <LoadingIndicator
                        dotCount={3}
                        dotSpacing={3}
                        dotSize={4}
                      />
                    </div>
                  )}
                  {query && (
                    <button
                      onClick={() => setQuery("")}
                      className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 cursor-pointer"
                    >
                      <FontAwesomeIcon icon={faXmark} className="w-4 h-3" />
                    </button>
                  )}
                </div>
              </div>

              {/* Filter Tabs */}
              {results.length > 0 && (
                <div className="px-4 py-2 border-b border-slate-200 dark:border-slate-700 flex gap-2 flex-wrap">
                  {(() => {
                    // Calculate counts for each type
                    const typeCounts: Record<string, number> = {};
                    const displayTypes = MAIN_ASSET_TYPES;
                    
                    displayTypes.forEach(type => {
                      typeCounts[type] = (groupedResults[type as SearchResultType] || []).length;
                    });
                    
                    // Also count "other" types (quick nav, workers, etc.)
                    const otherCount = results.filter(
                      (r) => !(MAIN_ASSET_TYPES as readonly string[]).includes(r.type),
                    ).length;
                    
                    const totalCount = results.length;
                    
                    return (
                      <>
                        <button
                          onClick={() => setSelectedFilter("all")}
                          className={`px-3 py-1 text-xs rounded-full transition-colors cursor-pointer ${
                            selectedFilter === "all"
                              ? "bg-indigo-500 text-white"
                              : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                          }`}
                        >
                          All ({totalCount})
                        </button>
                        {displayTypes.map(type => {
                          const count = typeCounts[type];
                          if (count === 0) return null;
                          return (
                            <button
                              key={type}
                              onClick={() => setSelectedFilter(type as SearchResultType)}
                              className={`px-3 py-1 text-xs rounded-full transition-colors cursor-pointer ${
                                selectedFilter === type
                                  ? "bg-indigo-500 text-white"
                                  : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                              }`}
                            >
                              {SEARCH_CATEGORIES[type as SearchResultType]?.label ??
                                type.charAt(0).toUpperCase() + type.slice(1) + "s"}{" "}
                              ({count})
                            </button>
                          );
                        })}
                        {otherCount > 0 && (
                          <button
                            onClick={() => setSelectedFilter("app")}
                            className={`px-3 py-1 text-xs rounded-full transition-colors cursor-pointer ${
                              selectedFilter === "app"
                                ? "bg-indigo-500 text-white"
                                : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600"
                            }`}
                          >
                            Other ({otherCount})
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Results */}
              <div className="max-h-96 overflow-y-auto">
                {isLoading && (
                  <div className="px-6 py-8 text-center text-slate-500">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-400 mx-auto mb-2"></div>
                    Searching...
                  </div>
                )}

                {!isLoading &&
                  !isApiLoading &&
                  !isSearching &&
                  results.length === 0 &&
                  query.length >= 2 &&
                  lastSearchedQuery.current === query && (
                    <div className="px-6 py-8 text-center text-slate-500">
                      No results found for &ldquo;{query}&rdquo;
                    </div>
                  )}

                {/* Debug info - remove this later */}

                {!isLoading && results.length > 0 && (
                  <div ref={resultsContainerRef} className="py-2">
                    {/* Merge all Quick Navigation types into one section */}
                    {(() => {
                      const quickNavTypes = Object.entries(SEARCH_CATEGORIES)
                        .filter(([_, cat]) => cat.label === "Quick Navigation")
                        .map(([type]) => type);
                      
                      const { quickNavResults, otherSections } =
                        partitionSearchResultsForFilter(
                          groupedResults,
                          quickNavTypes,
                          selectedFilter,
                        );
                      return (
                        <>
                          {(() => {
                            let globalIndex = 0;
                            return (
                              <>
                                {quickNavResults.length > 0 && (
                                  <div className="">
                                    <div className="px-6 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider">
                                      Quick Navigation
                                    </div>
                                    <div className="p-2 px-4">
                                      {quickNavResults.map((result) => {
                                        const currentIndex = globalIndex++;
                                        const isSelected =
                                          currentIndex === selectedIndex;
                                        return (
                                          <div key={result.id}>
                                            <button
                                              data-selected-index={currentIndex}
                                              onClick={() =>
                                                handleResultClick(result)
                                              }
                                              className={`w-full px-4 py-3 text-left rounded-xl transition-all duration-150 mb-2 cursor-pointer ${
                                                isSelected
                                                  ? "bg-slate-100 dark:bg-white/10"
                                                  : "hover:bg-slate-100 dark:hover:bg-white/5"
                                              }`}
                                              onMouseEnter={() =>
                                                setSelectedIndex(currentIndex)
                                              }
                                            >
                                              <div className="flex items-center gap-3">
                                                <div
                                                  className={`w-8 h-8 flex text-xl items-center justify-center rounded-lg transition-colors duration-150 ${
                                                    isSelected
                                                      ? "text-indigo-600 dark:text-indigo-400"
                                                      : "text-slate-500"
                                                  }`}
                                                >
                                                  {result.icon}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <div className={`font-medium truncate transition-colors duration-150 ${
                                                    isSelected
                                                      ? "text-slate-900 dark:text-white"
                                                      : "text-slate-700 dark:text-slate-300"
                                                  }`}>
                                                    {result.title}
                                                  </div>
                                                  {result.description && (
                                                    <div className="text-sm text-slate-500 truncate">
                                                      {result.description}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                                {otherSections.map(([type, typeResults]) => (
                                  <div key={type} className="">
                                    <div className="px-6 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider">
                                      {SEARCH_CATEGORIES[
                                        type as SearchResultType
                                      ]?.label || type}
                                    </div>
                                    <div className="p-2 px-4">
                                      {typeResults.map((result) => {
                                        const currentIndex = globalIndex++;
                                        const isSelected =
                                          currentIndex === selectedIndex;
                                        return (
                                          <div key={result.id}>
                                            <button
                                              data-selected-index={currentIndex}
                                              onClick={() =>
                                                handleResultClick(result)
                                              }
                                              className={`w-full px-4 py-3 text-left rounded-xl transition-all duration-150 mb-2 cursor-pointer ${
                                                isSelected
                                                  ? "bg-slate-100 dark:bg-white/10"
                                                  : "hover:bg-slate-100 dark:hover:bg-white/5"
                                              }`}
                                              onMouseEnter={() =>
                                                setSelectedIndex(currentIndex)
                                              }
                                            >
                                              <div className="flex items-center gap-3">
                                                <div
                                                  className={`w-8 h-8 flex text-xl items-center justify-center rounded-lg transition-colors duration-150 ${
                                                    isSelected
                                                      ? "text-indigo-600 dark:text-indigo-400"
                                                      : "text-slate-500"
                                                  }`}
                                                >
                                                  {result.icon}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                  <div className={`font-medium truncate transition-colors duration-150 ${
                                                    isSelected
                                                      ? "text-slate-900 dark:text-white"
                                                      : "text-slate-700 dark:text-slate-300"
                                                  }`}>
                                                    {result.title}
                                                  </div>
                                                  {result.description && (
                                                    <div className="text-sm text-slate-500 truncate">
                                                      {result.description}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            </button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))}
                                {displayResults.length >= 30 && (
                                  <div className="px-6 py-3 text-center text-xs text-slate-500 border-t border-slate-200 dark:border-slate-700/50">
                                    Showing first 30 results. Refine your search for more specific results.
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
